import { useState } from "react";
import { Home, MapPin, Bed, Bath, Square, TrendingUp, Mail, ShieldCheck, DollarSign, ArrowRight, BarChart3, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

const HomeEstimator = () => {
  const { toast } = useToast();
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [sqft, setSqft] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [propertyType, setPropertyType] = useState("Single Family");
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"form" | "email" | "results">("form");
  const [estimate, setEstimate] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const fullAddress = `${address}${city ? `, ${city}` : ''}${state ? `, ${state}` : ''} ${zipCode || ''}`.trim();

  const calculate = async () => {
    if (!address || !zipCode) {
      toast({ title: "Please enter your street address and zip code", variant: "destructive" });
      return;
    }

    setIsCalculating(true);
    setApiError(null);

    try {
      const { data, error } = await supabase.functions.invoke('hasdata-value-estimate', {
        body: {
          address: fullAddress,
          zipCode,
          squareFootage: sqft ? parseInt(sqft) : undefined,
          bedrooms: bedrooms ? parseInt(bedrooms) : undefined,
          bathrooms: bathrooms ? parseFloat(bathrooms) : undefined,
          propertyType,
        },
      });

      if (error) throw error;

      if (data?.error === 'property_not_found') {
        setApiError(data.message);
        setIsCalculating(false);
        return;
      }

      if (data?.price || data?.priceRangeLow || data?.priceRangeHigh) {
        setEstimate(data);
        setStep("email");
      } else {
        setApiError("We couldn't generate an estimate for this address. Please double-check the details and try again.");
      }
    } catch (err: any) {
      console.error('Value estimate error:', err);
      setApiError("Something went wrong. Please try again.");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email || !email.includes("@")) {
      toast({ title: "Please enter a valid email", variant: "destructive" });
      return;
    }

    setIsSubmittingEmail(true);
    try {
      await (supabase as any).from("estimate_leads").insert({
        email,
        sqft: parseInt(sqft) || null,
        bedrooms: parseInt(bedrooms) || null,
        bathrooms: parseFloat(bathrooms) || null,
        market_tier: zipCode,
        condition: propertyType,
        estimated_low: estimate?.priceRangeLow || null,
        estimated_mid: estimate?.price || null,
        estimated_high: estimate?.priceRangeHigh || null,
      });
    } catch (e) {
      // Silently continue
    }
    setStep("results");
    setIsSubmittingEmail(false);
  };

  const reset = () => {
    setAddress("");
    setCity("");
    setState("");
    setZipCode("");
    setSqft("");
    setBedrooms("");
    setBathrooms("");
    setPropertyType("Single Family");
    setEmail("");
    setEstimate(null);
    setStep("form");
    setApiError(null);
  };

  const comps = estimate?.comparables || [];
  const savingsAmount = estimate?.price ? Math.round(estimate.price * 0.05 / 100) * 100 : 0;

  return (
    <section className="py-20 sm:py-28 bg-card relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-5xl mx-auto px-6 relative">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            What's your home <span className="text-primary">really</span> worth?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Get an instant, data-driven valuation based on real market comps — not guesswork. Enter your address to see what buyers are actually paying.
          </p>
        </div>

        <div className="bg-background rounded-2xl border border-border shadow-xl p-6 sm:p-10">
          {step === "form" && (
            <>
              {/* Address row - full width */}
              <div className="mb-6">
                <label className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-primary" /> Street Address *
                </label>
                <Input
                  placeholder="e.g. 123 Main Street"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="bg-card border-border text-base py-3"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">City</label>
                  <Input
                    placeholder="e.g. Miami"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="bg-card border-border"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">State</label>
                  <Input
                    placeholder="e.g. FL"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="bg-card border-border"
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Zip Code *</label>
                  <Input
                    placeholder="e.g. 33101"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    className="bg-card border-border"
                    maxLength={5}
                  />
                </div>
              </div>

              {/* Optional property details */}
              <div className="border-t border-border pt-6 mb-6">
                <p className="text-sm text-muted-foreground mb-4">Optional — add details for a more precise estimate:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <Square className="h-3.5 w-3.5 text-primary" /> Sq Ft
                    </label>
                    <Input
                      type="number"
                      placeholder="1,800"
                      value={sqft}
                      onChange={(e) => setSqft(e.target.value)}
                      className="bg-card border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <Bed className="h-3.5 w-3.5 text-primary" /> Beds
                    </label>
                    <Select value={bedrooms} onValueChange={setBedrooms}>
                      <SelectTrigger className="bg-card border-border" aria-label="Bedrooms">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {["1", "2", "3", "4", "5", "6"].map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <Bath className="h-3.5 w-3.5 text-primary" /> Baths
                    </label>
                    <Select value={bathrooms} onValueChange={setBathrooms}>
                      <SelectTrigger className="bg-card border-border" aria-label="Bathrooms">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {["1", "1.5", "2", "2.5", "3", "3.5", "4"].map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <Building className="h-3.5 w-3.5 text-primary" /> Type
                    </label>
                    <Select value={propertyType} onValueChange={setPropertyType}>
                      <SelectTrigger className="bg-card border-border" aria-label="Property type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single Family">Single Family</SelectItem>
                        <SelectItem value="Condo">Condo</SelectItem>
                        <SelectItem value="Townhouse">Townhouse</SelectItem>
                        <SelectItem value="Multi Family">Multi Family</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {apiError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
                  <p className="text-destructive text-sm">{apiError}</p>
                </div>
              )}

              <div className="text-center">
                <Button
                  onClick={calculate}
                  disabled={!address || !zipCode || isCalculating}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-3 text-base shadow-md hover:shadow-lg transition-all duration-200"
                  size="lg"
                >
                  {isCalculating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Analyzing Market Data...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Get My Home Value <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </div>
            </>
          )}

          {step === "email" && (
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">Your valuation is ready!</h3>
              <p className="text-muted-foreground mb-6">
                Enter your email to see your data-driven home value with comparable sales.
              </p>
              <div className="space-y-4">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                  className="bg-card border-border text-center text-lg py-3"
                />
                <Button
                  onClick={handleEmailSubmit}
                  disabled={!email || !email.includes("@") || isSubmittingEmail}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 text-base shadow-md"
                  size="lg"
                >
                  {isSubmittingEmail ? "Loading..." : "Show My Valuation"}
                </Button>
              </div>
              <div className="flex items-center justify-center gap-2 mt-5 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>We promise — no spam, ever. Just your estimate.</span>
              </div>
            </div>
          )}

          {step === "results" && estimate && (
            <div>
              {/* Main value display */}
              <div className="text-center mb-10">
                <p className="text-muted-foreground mb-1 text-sm font-medium uppercase tracking-wider">Estimated Market Value</p>
                <p className="text-xs text-muted-foreground mb-4">{fullAddress}</p>

                <div className="flex items-end justify-center gap-6 sm:gap-10 mb-6">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Low</p>
                    <p className="text-xl sm:text-2xl font-bold text-muted-foreground">
                      ${(estimate.priceRangeLow || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-primary font-semibold mb-1">Suggested List Price</p>
                    <p className="text-4xl sm:text-5xl font-extrabold text-primary">
                      ${(estimate.price || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">High</p>
                    <p className="text-xl sm:text-2xl font-bold text-muted-foreground">
                      ${(estimate.priceRangeHigh || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Value bar visualization */}
                <div className="max-w-md mx-auto mb-8">
                  <div className="h-3 bg-muted rounded-full overflow-hidden relative">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary/50 via-primary to-primary/50 rounded-full"
                      style={{ left: '15%', right: '15%' }}
                    />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full border-2 border-background shadow-md"
                      style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
                    />
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-xl p-5 mb-8 max-w-lg mx-auto">
                  <p className="text-sm text-accent-foreground font-medium">
                    💰 By listing with REALV3 at <span className="font-bold">0.50% flat fee</span>, you'd keep an estimated{' '}
                    <span className="font-bold text-primary">${savingsAmount.toLocaleString()}</span> more than paying a traditional 5–6% commission.
                  </p>
                </div>
              </div>

              {/* Comparable Sales */}
              {comps.length > 0 && (
                <div className="mb-10">
                  <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Comparable Sales Near You
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {comps.slice(0, 6).map((comp: any, i: number) => (
                      <div key={i} className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow">
                        <p className="font-semibold text-primary text-lg mb-1">
                          ${(comp.price || comp.correlationPrice || 0).toLocaleString()}
                        </p>
                        <p className="text-sm text-foreground mb-2 line-clamp-1">
                          {comp.formattedAddress || comp.address || 'Address not available'}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {comp.bedrooms && (
                            <span className="flex items-center gap-1">
                              <Bed className="h-3 w-3" /> {comp.bedrooms}
                            </span>
                          )}
                          {comp.bathrooms && (
                            <span className="flex items-center gap-1">
                              <Bath className="h-3 w-3" /> {comp.bathrooms}
                            </span>
                          )}
                          {comp.squareFootage && (
                            <span className="flex items-center gap-1">
                              <Square className="h-3 w-3" /> {comp.squareFootage.toLocaleString()} sqft
                            </span>
                          )}
                        </div>
                        {comp.distance && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {comp.distance.toFixed(1)} mi away
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => window.location.href = '/list-property'}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8"
                  size="lg"
                >
                  List My Home Now
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  className="border-border text-muted-foreground hover:text-foreground"
                  size="lg"
                >
                  Try Another Address
                </Button>
              </div>

              <p className="text-xs text-muted-foreground mt-6 text-center">
                Valuations are based on real market data and comparable sales via RentCast AVM. For the most accurate pricing, list your home and let buyers compete.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeEstimator;
